import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, LogOut, Trash2, Inbox, ArrowLeft, Download } from "lucide-react";
import { API, useAuth, formatApiError } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const SERVICE_LABELS = {
  website: "Website Development",
  "graphic-design": "Graphic Design",
  "video-editing": "Video Editing",
  "ai-video": "AI Video Creation",
  "social-media": "Social Media Mgmt",
  "meta-marketing": "Meta Marketing",
  other: "Other",
};

const STATUS_STYLES = {
  new: "text-sky-300 border-sky-400/30 bg-sky-400/10",
  contacted: "text-amber-300 border-amber-400/30 bg-amber-400/10",
  closed: "text-emerald-300 border-emerald-400/30 bg-emerald-400/10",
};

const FILTERS = ["all", "new", "contacted", "closed"];

function LoginForm() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-password-modal" className="min-h-screen flex items-center justify-center px-4 bg-[#090A0F]">
      <form onSubmit={submit} className="w-full max-w-sm glass-card border-amber-500/15 p-8 flex flex-col gap-5">
        <div className="flex flex-col items-center gap-3 mb-2">
          <img src="/assets/logo-wide.png" alt="M&M Productions" className="h-20 w-auto" />
          <h1 className="font-heading text-xl font-semibold" data-testid="admin-login-title">Admin Portal</h1>
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="admin-email" className="text-slate-300">Email</Label>
          <Input id="admin-email" data-testid="admin-login-email-input" type="email" required value={email}
            onChange={(e) => setEmail(e.target.value)} autoComplete="email"
            className="bg-[#0C0E14] border-white/10 focus:border-amber-400/50 h-11" />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="admin-password" className="text-slate-300">Password</Label>
          <Input id="admin-password" data-testid="admin-login-password-input" type="password" required value={password}
            onChange={(e) => setPassword(e.target.value)} autoComplete="current-password"
            className="bg-[#0C0E14] border-white/10 focus:border-amber-400/50 h-11" />
        </div>
        <button type="submit" data-testid="admin-login-submit-btn" disabled={loading}
          className="btn-fill notched-sm mt-2 inline-flex items-center justify-center gap-2 border border-amber-400/50 bg-transparent px-6 py-3 text-sm font-bold text-amber-300 hover:text-black hover:shadow-[0_0_30px_rgba(212,175,55,0.35)] transition-[color,box-shadow] duration-300 disabled:opacity-60">
          <Lock className="h-4 w-4" />
          {loading ? "Signing in…" : "Sign In"}
        </button>
        <Link to="/" data-testid="admin-login-back-link" className="text-center text-xs text-slate-500 hover:text-amber-300 transition-colors">
          ← Back to site
        </Link>
      </form>
    </div>
  );
}

function Dashboard() {
  const { logout, setUnread, refreshUnread } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState({});
  const [savingNote, setSavingNote] = useState(null);

  const saveNotes = async (id) => {
    setSavingNote(id);
    try {
      await axios.patch(`${API}/inquiries/${id}/notes`, { notes: notes[id] ?? "" }, { withCredentials: true });
      toast.success("Note saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingNote(null);
    }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/inquiries`, { withCredentials: true });
      setInquiries(data);
      setNotes(Object.fromEntries(data.map((i) => [i.id, i.notes || ""])));
      refreshUnread();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [refreshUnread]);

  useEffect(() => {
    (async () => {
      await load();
      try {
        await axios.post(`${API}/inquiries/mark-all-read`, {}, { withCredentials: true });
        setUnread(0);
      } catch { /* ignore */ }
    })();
    const poll = setInterval(() => load(), 30000);
    return () => clearInterval(poll);
  }, [load, setUnread]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/inquiries/${id}/status`, { status }, { withCredentials: true });
      setInquiries((list) => list.map((i) => (i.id === id ? { ...i, status } : i)));
      toast.success(`Marked as ${status}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/inquiries/${id}`, { withCredentials: true });
      setInquiries((list) => list.filter((i) => i.id !== id));
      toast.success("Inquiry deleted");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const visible = filter === "all" ? inquiries : inquiries.filter((i) => i.status === filter);
  const counts = FILTERS.reduce((acc, f) => ({ ...acc, [f]: f === "all" ? inquiries.length : inquiries.filter((i) => i.status === f).length }), {});

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#090A0F] px-4 sm:px-6 lg:px-12 py-8 text-center">
      <div className="max-w-5xl mx-auto">
        <header className="flex flex-wrap items-center justify-center gap-4 mb-8">
          <div className="flex items-center gap-4">
            <img src="/assets/logo-wide.png" alt="M&M" className="h-8 w-auto" />
            <div>
              <h1 className="font-heading text-xl font-semibold" data-testid="admin-dashboard-title">Lead Inbox</h1>
              <p className="text-xs text-slate-500" data-testid="admin-total-count">{inquiries.length} total inquiries</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-back-to-site-btn" className="inline-flex items-center gap-2 text-xs text-slate-400 hover:text-amber-300 border border-white/10 rounded-full px-4 py-2 transition-colors">
              <ArrowLeft className="h-3.5 w-3.5" /> Site
            </Link>
            <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 text-xs text-slate-400 hover:text-red-300 border border-white/10 rounded-full px-4 py-2 transition-colors">
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </header>

        <div className="flex flex-wrap justify-center gap-2 mb-8" data-testid="admin-status-filters">
          {FILTERS.map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`admin-status-filter-${f}`}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold capitalize transition-all duration-300 border ${
                filter === f ? "border-amber-400/50 bg-amber-400/10 text-amber-300" : "border-white/10 text-slate-400 hover:border-amber-400/30"
              }`}>
              {f} ({counts[f]})
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-slate-500 text-sm" data-testid="admin-loading">Loading inquiries…</p>
        ) : visible.length === 0 ? (
          <div data-testid="admin-empty-state" className="glass-card p-12 flex flex-col items-center gap-3 text-center">
            <Inbox className="h-8 w-8 text-slate-600" />
            <p className="text-slate-400 text-sm">No inquiries here yet.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4" data-testid="admin-inbox-table">
            {visible.map((inq) => (
              <article key={inq.id} data-testid={`inquiry-item-${inq.id}`}
                className={`rounded-2xl border p-6 sm:p-8 transition-all duration-300 ${inq.read ? "border-white/10 bg-[#12141C]" : "border-amber-400/30 bg-[#14161f] shadow-[0_0_25px_rgba(212,175,55,0.08)]"}`}>
                <div className="flex flex-col items-center text-center gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-center gap-3 flex-wrap">
                      <h3 className="font-heading text-xl sm:text-2xl font-semibold text-slate-100" data-testid={`inquiry-name-${inq.id}`}>{inq.name}</h3>
                      <span className={`text-xs font-bold uppercase tracking-[0.15em] rounded-full border px-3 py-1 ${STATUS_STYLES[inq.status]}`}
                        data-testid={`inquiry-status-badge-${inq.id}`}>{inq.status}</span>
                      {!inq.read && <span className="h-2.5 w-2.5 rounded-full bg-amber-400" data-testid={`inquiry-unread-dot-${inq.id}`} />}
                    </div>
                    <p className="mt-1.5 text-sm text-slate-400">
                      {SERVICE_LABELS[inq.service] || inq.service}
                      {inq.budget ? ` · Budget: ${inq.budget}` : ""}
                      {" · "}{new Date(inq.created_at).toLocaleString()}
                    </p>
                    <p className="mt-1.5 text-sm sm:text-base text-slate-300" data-testid={`inquiry-contact-${inq.id}`}>
                      {inq.email && <a href={`mailto:${inq.email}`} className="text-amber-300/90 hover:text-amber-200 transition-colors">{inq.email}</a>}
                      {inq.email && inq.phone && <span> · </span>}
                      {inq.phone && <a href={`tel:${inq.phone}`} className="text-amber-300/90 hover:text-amber-200 transition-colors">{inq.phone}</a>}
                    </p>
                    <p className="mt-3 text-base text-slate-200 leading-relaxed whitespace-pre-wrap" data-testid={`inquiry-details-${inq.id}`}>{inq.details}</p>
                    {inq.source && <p className="mt-2 text-sm text-slate-500">Heard via: {inq.source}</p>}
                    {inq.intake && (
                      <Link to={`/admin/intake/${inq.id}`} data-testid={`intake-toggle-${inq.id}`}
                        className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-amber-300/90 hover:text-amber-200 transition-colors">
                        Full Intake <span className="font-serif font-bold">❯</span>
                      </Link>
                    )}
                    {inq.files && inq.files.length > 0 && (
                      <div className="mt-3 flex flex-wrap justify-center gap-2" data-testid={`inquiry-files-${inq.id}`}>
                        {inq.files.map((fl) => (
                          <a key={fl.path} href={`${API}/files/${fl.path}`} data-testid={`file-download-${fl.name}`}
                            className="inline-flex items-center gap-2 notched-sm border border-amber-400/30 bg-amber-400/5 px-3 py-1.5 text-xs text-amber-200 hover:bg-amber-400/10 transition-colors">
                            <Download className="h-3.5 w-3.5" /> {fl.name}
                          </a>
                        ))}
                      </div>
                    )}
                    <div className="mt-5 w-full max-w-xl mx-auto text-left" data-testid={`notes-section-${inq.id}`}>
                      <Label htmlFor={`notes-${inq.id}`} className="text-xs uppercase tracking-[0.2em] text-slate-500">
                        Follow-Up Notes <span className="normal-case tracking-normal text-slate-600">(private)</span>
                      </Label>
                      <Textarea id={`notes-${inq.id}`} rows={2} value={notes[inq.id] ?? ""}
                        onChange={(e) => setNotes((m) => ({ ...m, [inq.id]: e.target.value }))}
                        placeholder="Where this conversation stands…"
                        data-testid={`inquiry-notes-${inq.id}`}
                        className="mt-2 bg-black/40 border-white/10 focus:border-amber-400/40 text-sm text-slate-200 resize-none" />
                      <div className="mt-2 flex justify-end">
                        <button type="button" onClick={() => saveNotes(inq.id)} disabled={savingNote === inq.id}
                          data-testid={`save-note-${inq.id}`}
                          className="text-xs font-semibold text-amber-300/90 hover:text-amber-200 border border-amber-400/30 hover:border-amber-400/50 rounded-full px-4 py-1.5 transition-all duration-300 disabled:opacity-50">
                          {savingNote === inq.id ? "Saving…" : "Save Note"}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <Select value={inq.status} onValueChange={(v) => updateStatus(inq.id, v)}>
                      <SelectTrigger data-testid={`inquiry-status-select-${inq.id}`} className="bg-[#0C0E14] border-white/10 h-9 w-32 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#12141C] border-white/10">
                        <SelectItem value="new" data-testid={`status-option-new-${inq.id}`}>New</SelectItem>
                        <SelectItem value="contacted" data-testid={`status-option-contacted-${inq.id}`}>Contacted</SelectItem>
                        <SelectItem value="closed" data-testid={`status-option-closed-${inq.id}`}>Closed</SelectItem>
                      </SelectContent>
                    </Select>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button data-testid={`inquiry-delete-btn-${inq.id}`} className="p-2 rounded-full border border-white/10 text-slate-500 hover:text-red-300 hover:border-red-400/40 transition-colors" aria-label="Delete inquiry">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-[#12141C] border-white/10">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete this inquiry?</AlertDialogTitle>
                          <AlertDialogDescription className="text-slate-400">
                            This permanently removes the inquiry from {inq.name}. This cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid={`inquiry-delete-cancel-${inq.id}`} className="bg-transparent border-white/10">Cancel</AlertDialogCancel>
                          <AlertDialogAction data-testid={`inquiry-delete-confirm-${inq.id}`} onClick={() => remove(inq.id)} className="bg-red-500/90 hover:bg-red-500 text-white">
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen bg-[#090A0F] flex items-center justify-center" data-testid="admin-loading-screen">
        <p className="text-slate-500 text-sm">Loading…</p>
      </div>
    );
  }
  return user ? <Dashboard /> : <LoginForm />;
}
